import { inject, Injectable } from '@angular/core';
import { CategoryPathCellType } from '@app/analyze-data/components/category-paths-cell-renderer/category-paths-cell-renderer.component';
import { COLUMN_STATS_EMPTY, CountryDropdownOption } from '@app/analyze-data/constants/analyze-data.constants';
import { SURFACE_TAXONOMIES } from '@app/category-taxonomy/models/constants/category-taxonomy.constants';
import { CategoryPathsCategoryGroups, CategorySelectItem } from '@app/category-taxonomy/models/types/category-paths-display.type';
import { SurfaceTaxonomy } from '@app/category-taxonomy/models/types/surface-taxonomy.type';
import { CategoryPath } from '@app/category-taxonomy/services/responses/category-paths.response';
import { isEmpty } from '@app/core/functions/isEmpty';
import { ChannelClient } from '@app/core/models/enums/channel-client.enum';
import { TAXONOMY_SERVICE_CLIENT_PREFIX } from '@app/exports/models/constants/missing-fields.constants';
import { MissingFieldsService } from '@app/exports/services/missing-fields.service';
import { IconDefinition, faCircleInfo, faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

export const ITEM_TYPE_DISPLAY_LIMIT: number = 5;

@Injectable({
    providedIn: 'root'
})
export class CategoryPathsUtilitiesService {

    /** Surface taxonomies the Categorization drawer offers, keyed like SURFACE_TAXONOMIES. */
    private static readonly SURFACE_CATEGORIZATION_TAXONOMIES: [string, SurfaceTaxonomy][] = Array.from(SURFACE_TAXONOMIES)
        .filter(([, taxonomy]: [string, SurfaceTaxonomy]) => taxonomy.hasCategories !== false);

    private readonly missingFieldsService: MissingFieldsService = inject(MissingFieldsService);

    readonly pathIcon: IconDefinition = faCircleInfo;
    readonly errorIcon: IconDefinition = faExclamationCircle;

    clientIsAmazon(client: string): boolean {
        return client?.toLowerCase().includes(ChannelClient.Amazon.toLowerCase());
    }

    /**
     * Returns true only for the base `google` channel client (case-insensitive).
     *
     * This method backs `shouldDisplayPath()` and the Taxonomy page's
     * `shouldDisplayPath` getter, both of which hide category paths when the
     * underlying taxonomy stores the path as the value (Google Product
     * Category). The taxonomy table historically stores this taxonomy under
     * `client = 'google'`, so the strict match is intentional and preserves
     * pre-existing behavior for regional Google clients on those consumers.
     *
     * For the AI bulk-categorization flow — where regional Google channels
     * (`google_de`, `google_es`, …) must be grouped with `google` — use
     * `clientIsGoogleFamily()` instead.
     */
    clientIsGoogle(client: string): boolean {
        return client?.toLowerCase() === ChannelClient.Google;
    }

    /**
     * Returns true for the Google Shopping channel family used by the AI
     * categorization bulk flow (base + per-country regional clients).
     *
     * Matches (case-insensitive):
     *   - `google`
     *   - `google_<ISO 3166-1 alpha-2>` (e.g. `google_de`, `google_es`,
     *     `google_fr`, `google_gb`, `google_it`)
     *
     * Intentionally excludes:
     *   - `google_product_type`, which is a distinct categorization client
     *     with its own taxonomy and must not fan-out to GPC + product_type.
     *   - Any other `google_*` suffix that is not a 2-letter country code.
     */
    clientIsGoogleFamily(client: string): boolean {
        return /^google(_[a-z]{2})?$/.test(client?.toLowerCase() ?? '');
    }

    clientIsTarget(client: string): boolean {
        return client === ChannelClient.Target;
    }

    clientIsTikTok(client: string): boolean {
        return client === ChannelClient.TikTok;
    }

    clientIsTikTokExpansion(client: string): boolean {
        return client?.toLowerCase().includes(ChannelClient.TikTokExpansion.toLowerCase());
    }

    clientIsFacebook(client: string): boolean {
        return client?.toLowerCase().includes(ChannelClient.Facebook.toLowerCase());
    }

    clientIsTaxonomyService(client: string): boolean {
        return client?.startsWith(TAXONOMY_SERVICE_CLIENT_PREFIX) ?? false;
    }

    /**
     * Splits a TaaS client (`taxonomy_service:{key}:{version}`) or export key (`{key}:{version}`).
     * Keys compare like the platform's TaxonomyKeyAliases::normalize: case-insensitive, hyphens and underscores alike.
     */
    taxonomyServiceKeyParts(value: string): { key: string; version: string; } {
        const [key, version = ''] = this.missingFieldsService.normalizeChannelKey(value).split(':');
        return { key: key.trim().toLowerCase().replace(/-/g, '_'), version };
    }

    /** Whether the Surface Categorization dropdown offers this channel client. TaaS clients match in any version. */
    isSurfaceChannelClient(client: string): boolean {
        if (this.clientIsTaxonomyService(client)) {
            return this.isSurfaceTaxonomyServiceKey(client);
        }
        return Boolean(client) && CategoryPathsUtilitiesService.SURFACE_CATEGORIZATION_TAXONOMIES.some(
            ([, taxonomy]: [string, SurfaceTaxonomy]) => taxonomy.legacyClient === client
        );
    }

    /** Whether the Surface Categorization dropdown offers the taxonomy of this TaaS client or export key, in any version. */
    isSurfaceTaxonomyServiceKey(value: string): boolean {
        const { key } = this.taxonomyServiceKeyParts(value);
        return CategoryPathsUtilitiesService.SURFACE_CATEGORIZATION_TAXONOMIES.some(([id]: [string, SurfaceTaxonomy]) =>
            this.clientIsTaxonomyService(id) && this.taxonomyServiceKeyParts(id).key === key
        );
    }

    /** TaaS taxonomy keys already carry their region, so TaaS clients send an empty one. */
    getCategoryPathsRegion(country: CountryDropdownOption): string {
        return this.clientIsTaxonomyService(country.client) ? '' : country.region;
    }

    getDisplayedValue(config: CategoryPathCellType): string {
        return config.value === '' ? COLUMN_STATS_EMPTY : config.value;
    }

    getDisplayedPath(categoryPath: CategoryPath): string {
        return `${categoryPath.path}${(categoryPath.query ? ` | ${categoryPath.query}` : '')}`;
    }

    /** Should we display paths at all? In the case of Google, the paths are the same as the values.
     */
    shouldDisplayPath(country: CountryDropdownOption): boolean {
        return country ? !this.clientIsGoogle(country.client) : false;
    }

    /** Should we display the path along with the value? This is good for cases when there's only one path,
     * unlike with Amazon.
     */
    shouldDisplayPathInline(country: CountryDropdownOption, category: CategorySelectItem): boolean {
        return this.shouldDisplayPath(country)
            && !(isEmpty(category?.groups) || isEmpty(category?.groups[0]))
            && !this.clientIsAmazon(country.client)
            && category.groups.length === 1
            && category.groups[0].length === 1;
    }

    /** Should we just display the path in a modal format? This is good for cases when there are multiple
     * paths, like with Amazon.
     */
    shouldDisplayPathModal(country: CountryDropdownOption, category: CategorySelectItem): boolean {
        return this.shouldDisplayPath(country)
            && !(isEmpty(category?.groups) || isEmpty(category?.groups[0]))
            && (this.clientIsAmazon(country.client) || category.groups.length > 1);
    }

    getComparatorFn(): (valueA: CategoryPathCellType, valueB: CategoryPathCellType) => number {
        return (valueA: CategoryPathCellType, valueB: CategoryPathCellType): number => {
            const a: string = valueA.value.toLowerCase();
            const b: string = valueB.value.toLowerCase();
            if (a === b) {
                return 0;
            }
            return (a > b) ? 1 : -1;
        }
    }

    /**
     * For use when filtering by Amazon product_type, check if the length of item types for a particular
     * group of product types exceeds the ITEM_TYPE_DISPLAY_LIMIT
     */
    hasMoreItemTypesThanLimit(groupedPaths: CategoryPathsCategoryGroups): boolean {
        return groupedPaths.some((paths) => paths.length > ITEM_TYPE_DISPLAY_LIMIT);
    }

    /**
     * Checks the length of the category paths array within each array of categories, and if there is more
     * than one path, return true
     */
    hasMultiplePathsPerCategory(groupedPaths: CategoryPathsCategoryGroups): boolean {
        return groupedPaths.some((paths) => paths.some((pathArray) => pathArray.length > 1));
    }
}
